import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { OnboardingStatusResponse } from '../types';
import OnboardingProgressBar from './OnboardingProgressBar';
import OnboardingWelcome from './OnboardingWelcome';
import OnboardingToS from './OnboardingToS';
import OnboardingProfile from './OnboardingProfile';
import OnboardingContact from './OnboardingContact';
import OnboardingPayment from './OnboardingPayment';
import OnboardingFairReview from './OnboardingFairReview';
import OnboardingComplete from './OnboardingComplete';
import { extractApiError } from '../utils/errorUtils';

/**
 * Main container for Fair Manager Onboarding flow
 * Issue #1455 - 7-step onboarding with token-based authentication
 *
 * Features:
 * - Token validation and expiration handling
 * - Progress tracking and resume capability
 * - Step-by-step navigation
 * - Accessibility compliant (WCAG 2.1 AA)
 */
const FairManagerOnboarding: React.FC = () => {
  const { token } = useParams<{ token: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onboardingData, setOnboardingData] = useState<OnboardingStatusResponse | null>(null);
  const [currentStep, setCurrentStep] = useState<string>('welcome');

  // Fetch onboarding status on mount
  useEffect(() => {
    const fetchOnboardingStatus = async () => {
      if (!token) {
        setError('Invalid onboarding link. Please check your email for the correct link.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await api.getOnboardingStatus(token);
        setOnboardingData(data);
        setCurrentStep(data.onboarding.current_step);
        setError(null);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        setError(apiError.message || 'Failed to load onboarding information');
        console.error('Failed to fetch onboarding status:', apiError);
      } finally {
        setLoading(false);
      }
    };

    fetchOnboardingStatus();
  }, [token]);

  // Handle step navigation
  const handleStepComplete = async (step: string, data?: Record<string, unknown>) => {
    if (!token) return;

    try {
      await api.updateOnboardingStep(token, step, data);

      // Refresh onboarding status
      const updatedData = await api.getOnboardingStatus(token);
      setOnboardingData(updatedData);
      setCurrentStep(updatedData.onboarding.current_step);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message || 'Failed to update progress');
      console.error('Failed to update onboarding step:', apiError);
      throw err; // Re-throw so step components can handle it
    }
  };

  // Handle ToS acceptance
  const handleToSAccept = async () => {
    if (!token) return;

    try {
      await api.acceptOnboardingToS(token);
      await handleStepComplete('profile'); // Move to next step
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message || 'Failed to accept Terms of Service');
      console.error('Failed to accept ToS:', apiError);
      throw err;
    }
  };

  // Handle onboarding completion
  const handleComplete = async () => {
    if (!token) return;

    try {
      await api.completeOnboarding(token);

      // Navigate to fair manager dashboard
      setTimeout(() => {
        window.location.href = '/fairmanager/dashboard';
      }, 3000);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message || 'Failed to complete onboarding');
      console.error('Failed to complete onboarding:', apiError);
      throw err;
    }
  };

  // Get completed steps for progress bar
  const getCompletedSteps = (): string[] => {
    if (!onboardingData) return [];

    const steps: string[] = [];
    const { onboarding } = onboardingData;

    if (onboarding.tos_accepted) steps.push('tos');
    if (onboarding.profile_completed) steps.push('profile');
    if (onboarding.contact_info_completed) steps.push('contact');
    if (onboarding.payment_setup_completed) steps.push('payment');
    if (onboarding.fair_review_completed) steps.push('fair_review');
    if (onboarding.completed) steps.push('done');

    return steps;
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto" role="status" aria-label="Loading onboarding information"></div>
          <p className="mt-4 text-gray-600">Loading your onboarding...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !onboardingData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="mt-4 text-xl font-semibold text-gray-900">Onboarding Link Error</h2>
            <p className="mt-2 text-gray-600">{error}</p>
            <div className="mt-6">
              <a
                href="/"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
              >
                Return to Home
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!onboardingData) return null;

  // Render current step
  const renderStep = () => {
    const stepProps = {
      token: token!,
      onboardingData,
      onStepComplete: handleStepComplete,
      error,
    };

    switch (currentStep) {
      case 'welcome':
        return <OnboardingWelcome {...stepProps} />;
      case 'tos':
        return <OnboardingToS {...stepProps} onAccept={handleToSAccept} />;
      case 'profile':
        return <OnboardingProfile {...stepProps} />;
      case 'contact':
        return <OnboardingContact {...stepProps} />;
      case 'payment':
        return <OnboardingPayment {...stepProps} />;
      case 'fair_review':
        return <OnboardingFairReview {...stepProps} />;
      case 'done':
        return <OnboardingComplete {...stepProps} onComplete={handleComplete} />;
      default:
        return <OnboardingWelcome {...stepProps} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Fair Manager Onboarding</h1>
          <p className="mt-2 text-gray-600">Welcome! Let's get you set up to manage your fair.</p>
        </div>

        {/* Progress Bar */}
        <OnboardingProgressBar
          currentStep={currentStep}
          completedSteps={getCompletedSteps()}
        />

        {/* Step Content */}
        <div className="mt-8">
          {renderStep()}
        </div>

        {/* Error banner (non-blocking) */}
        {error && onboardingData && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-md p-4" role="alert">
            <div className="flex">
              <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FairManagerOnboarding;
